import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "@helpers/translate";
import "./index.sass";

const CHART_WIDTH = 1000;
const CHART_HEIGHT = 360;
const CHART_PADDING = {
  top: 32,
  right: 14,
  bottom: 12,
  left: 10,
};
const TOOLTIP_WIDTH = 220;
const MOBILE_TOOLTIP_WIDTH = 180;
const TOOLTIP_SCREEN_OFFSET = 8;

const formatCurrency = (value) => {
  const number = Number(value) || 0;

  if (number === 0) {
    return "$0";
  }

  const sign = number < 0 ? "-$" : "$";

  return `${sign}${new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(number))}`;
};

const formatAxisCurrency = (value) => {
  const number = Number(value) || 0;

  if (number === 0) {
    return "$0";
  }

  const sign = number < 0 ? "-$" : "$";
  const absValue = Math.abs(number);

  if (absValue >= 1000000) {
    const formattedValue = absValue / 1000000;
    const decimals = formattedValue < 10 && formattedValue % 1 !== 0 ? 1 : 0;

    return `${sign}${formattedValue.toFixed(decimals)}M`;
  }

  if (absValue >= 1000) {
    const formattedValue = absValue / 1000;
    const decimals = formattedValue < 10 && formattedValue % 1 !== 0 ? 1 : 0;

    return `${sign}${formattedValue.toFixed(decimals)}K`;
  }

  if (absValue < 10 && absValue % 1 !== 0) {
    return `${sign}${absValue.toFixed(1)}`;
  }

  return `${sign}${Math.round(absValue).toLocaleString("en-US")}`;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseChartDate = (date) => {
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    const [year, month, day] = date.split("-").map(Number);
    const localDate = new Date(year, month - 1, day);

    return Number.isNaN(localDate.getTime()) ? null : localDate;
  }

  const currentDate = new Date(date);

  return Number.isNaN(currentDate.getTime()) ? null : currentDate;
};

const formatDayMonth = (date) => {
  const currentDate = parseChartDate(date);

  if (!currentDate) {
    return String(date);
  }

  const day = String(currentDate.getDate()).padStart(2, "0");
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");

  return `${day}/${month}`;
};

const getPeriodSpanDays = (points) => {
  if (!points?.length) {
    return 0;
  }

  const startDate = parseChartDate(points[0].date);
  const endDate = parseChartDate(points[points.length - 1].date);

  if (!startDate || !endDate) {
    return Math.max(points.length - 1, 0);
  }

  const spanMs = endDate.getTime() - startDate.getTime();

  return Math.max(1, Math.ceil(spanMs / MS_PER_DAY));
};

const DAYS_IN_WEEK = 7;
const DAYS_IN_MONTH = 31;
const DAYS_FOR_MONTH_LABELS = 150;
const DAYS_IN_YEAR = 365;

const X_AXIS_GRANULARITY = {
  DAY: "day",
  MONTH: "month",
  YEAR: "year",
};

const MONTH_TRANSLATION_KEYS = [
  "markets.months.jan",
  "markets.months.feb",
  "markets.months.mar",
  "markets.months.apr",
  "markets.months.may",
  "markets.months.jun",
  "markets.months.jul",
  "markets.months.aug",
  "markets.months.sep",
  "markets.months.oct",
  "markets.months.nov",
  "markets.months.dec",
];

const IDEAL_X_LABEL_COUNT = 7;
const CUSTOM_RANGE_LABEL_COUNT = 5;

const getMonthsSince = (date, periodStartDate) => {
  const currentDate = parseChartDate(date);

  if (!currentDate || !periodStartDate) {
    return 0;
  }

  return (
    (currentDate.getFullYear() - periodStartDate.getFullYear()) * 12 +
    (currentDate.getMonth() - periodStartDate.getMonth())
  );
};

const getYearsSince = (date, periodStartDate) => {
  const currentDate = parseChartDate(date);

  if (!currentDate || !periodStartDate) {
    return 0;
  }

  return currentDate.getFullYear() - periodStartDate.getFullYear();
};

const getDaysSincePeriodStart = (date, periodStartDate) => {
  const currentDate = parseChartDate(date);

  if (!currentDate || !periodStartDate) {
    return 0;
  }

  return Math.round(
    (currentDate.getTime() - periodStartDate.getTime()) / MS_PER_DAY,
  );
};

const formatHourLabel = (date) => {
  const currentDate = parseChartDate(date);

  if (!currentDate) {
    return String(date);
  }

  return `${String(currentDate.getHours()).padStart(2, "0")}:00`;
};

const getXAxisGranularity = (interval, spanDays) => {
  if (interval === "hourly") {
    return X_AXIS_GRANULARITY.DAY;
  }

  if (interval === "monthly") {
    return X_AXIS_GRANULARITY.MONTH;
  }

  if (spanDays > DAYS_IN_YEAR) {
    return X_AXIS_GRANULARITY.YEAR;
  }

  if (spanDays >= DAYS_FOR_MONTH_LABELS) {
    return X_AXIS_GRANULARITY.MONTH;
  }

  return X_AXIS_GRANULARITY.DAY;
};

const getDailyLabelStepDays = (spanDays) => {
  if (spanDays <= DAYS_IN_WEEK) {
    return 1;
  }

  if (spanDays <= DAYS_IN_MONTH) {
    return 5;
  }

  const stepFromSpan = Math.round(spanDays / IDEAL_X_LABEL_COUNT);

  return Math.min(15, Math.max(10, stepFromSpan));
};

const getMonthLabelStep = (spanDays) => {
  const monthCount = Math.max(1, Math.ceil(spanDays / 30));

  if (monthCount <= IDEAL_X_LABEL_COUNT) {
    return 1;
  }

  return Math.ceil(monthCount / IDEAL_X_LABEL_COUNT);
};

const getYearLabelStep = (spanDays) => {
  const yearCount = Math.max(1, Math.ceil(spanDays / DAYS_IN_YEAR));

  if (yearCount <= IDEAL_X_LABEL_COUNT) {
    return 1;
  }

  return Math.ceil(yearCount / IDEAL_X_LABEL_COUNT);
};

const getXAxisTickStrategy = (interval, spanDays) => {
  const granularity = getXAxisGranularity(interval, spanDays);

  return {
    granularity,
    interval,
    spanDays,
    dayStep:
      granularity === X_AXIS_GRANULARITY.DAY
        ? getDailyLabelStepDays(spanDays)
        : null,
    monthStep:
      granularity === X_AXIS_GRANULARITY.MONTH
        ? getMonthLabelStep(spanDays)
        : null,
    yearStep:
      granularity === X_AXIS_GRANULARITY.YEAR
        ? getYearLabelStep(spanDays)
        : null,
  };
};

const isMonthBoundaryTick = (index, points) => {
  if (index === 0) {
    return true;
  }

  const previousDate = parseChartDate(points[index - 1].date);
  const currentDate = parseChartDate(points[index].date);

  if (!previousDate || !currentDate) {
    return false;
  }

  return (
    currentDate.getMonth() !== previousDate.getMonth() ||
    currentDate.getFullYear() !== previousDate.getFullYear()
  );
};

const isYearBoundaryTick = (index, points) => {
  if (index === 0) {
    return true;
  }

  const previousDate = parseChartDate(points[index - 1].date);
  const currentDate = parseChartDate(points[index].date);

  if (!previousDate || !currentDate) {
    return false;
  }

  return currentDate.getFullYear() !== previousDate.getFullYear();
};

const shouldShowXAxisLabel = (point, index, points, strategy) => {
  const isFirst = index === 0;
  const isLast = index === points.length - 1;

  if (strategy.interval === "hourly") {
    return isFirst || isLast || index % 3 === 0;
  }

  const periodStartDate = parseChartDate(points[0].date);

  if (!periodStartDate) {
    return isFirst || isLast;
  }

  const { granularity, interval, dayStep, monthStep, yearStep } = strategy;

  if (granularity === X_AXIS_GRANULARITY.DAY) {
    if (dayStep === 1) {
      return true;
    }

    const daysSinceStart = getDaysSincePeriodStart(point.date, periodStartDate);

    return isFirst || isLast || daysSinceStart % dayStep === 0;
  }

  if (granularity === X_AXIS_GRANULARITY.MONTH) {
    const monthsSinceStart = getMonthsSince(point.date, periodStartDate);

    if (interval === "monthly") {
      return isFirst || isLast || monthsSinceStart % monthStep === 0;
    }

    if (!isMonthBoundaryTick(index, points)) {
      return false;
    }

    return isFirst || isLast || monthsSinceStart % monthStep === 0;
  }

  if (granularity === X_AXIS_GRANULARITY.YEAR) {
    const yearsSinceStart = getYearsSince(point.date, periodStartDate);

    if (interval === "monthly") {
      return isFirst || isLast || yearsSinceStart % yearStep === 0;
    }

    if (!isYearBoundaryTick(index, points)) {
      return false;
    }

    return isFirst || isLast || yearsSinceStart % yearStep === 0;
  }

  return isFirst || isLast;
};

const getMonthLabel = (date, translate) => {
  const currentDate = parseChartDate(date);

  if (!currentDate) {
    return String(date);
  }

  const monthKey =
    MONTH_TRANSLATION_KEYS[currentDate.getMonth()] || MONTH_TRANSLATION_KEYS[0];

  return translate(monthKey);
};

const formatMonthShortLabel = (date, translate, withYear = false) => {
  const currentDate = parseChartDate(date);

  if (!currentDate) {
    return String(date);
  }

  const monthLabel = getMonthLabel(date, translate);

  if (!withYear) {
    return monthLabel;
  }

  return `${monthLabel} ${String(currentDate.getFullYear()).slice(-2)}`;
};

const formatXAxisLabel = (date, strategy, translate) => {
  const { granularity, interval, spanDays } = strategy;

  if (interval === "hourly") {
    return formatHourLabel(date);
  }

  if (interval === "monthly") {
    return formatMonthShortLabel(date, translate, spanDays > DAYS_IN_YEAR);
  }

  if (granularity === X_AXIS_GRANULARITY.YEAR) {
    const currentDate = parseChartDate(date);

    return currentDate ? String(currentDate.getFullYear()) : String(date);
  }

  if (granularity === X_AXIS_GRANULARITY.MONTH) {
    return formatMonthShortLabel(date, translate);
  }

  return formatDayMonth(date);
};

const formatTooltipDateLabel = (date, translate, interval) => {
  const currentDate = parseChartDate(date);

  if (!currentDate) {
    return String(date);
  }

  const month = getMonthLabel(date, translate);
  const day = currentDate.getDate();
  const year = currentDate.getFullYear();

  if (interval === "hourly") {
    const hour = String(currentDate.getHours()).padStart(2, "0");

    return `${day} ${month} ${hour}:00`;
  }

  if (interval === "monthly") {
    return `${month} ${year}`;
  }

  return `${month} ${day}`;
};

const getGeneratedRangeLabels = (points, strategy, translate) => {
  const startDate = parseChartDate(points[0]?.date);
  const endDate = parseChartDate(points[points.length - 1]?.date);

  if (!startDate || !endDate) {
    return [];
  }

  const count = CUSTOM_RANGE_LABEL_COUNT;
  const spanMs = endDate.getTime() - startDate.getTime();

  return Array.from({ length: count }, (_, index) => {
    const ratio = count <= 1 ? 0 : index / (count - 1);
    const currentDate = new Date(startDate.getTime() + spanMs * ratio);

    return {
      index: `generated-${index}`,
      date: currentDate.toISOString(),
      label: formatXAxisLabel(currentDate, strategy, translate),
      show: true,
    };
  });
};

const getXAxisLabels = (points, interval, spanDays, translate) => {
  const strategy = getXAxisTickStrategy(interval, spanDays);

  const labels = points
    .map((point, index) => ({
      index,
      date: point.date,
      label: formatXAxisLabel(point.date, strategy, translate),
      show: shouldShowXAxisLabel(point, index, points, strategy),
    }))
    .filter((item) => item.show);

  if (spanDays > DAYS_IN_MONTH && labels.length <= 2) {
    return getGeneratedRangeLabels(points, strategy, translate);
  }

  return labels;
};

const getChartY = (value, minValue, maxValue) => {
  const chartHeight = CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom;

  if (maxValue === minValue) {
    return CHART_PADDING.top + chartHeight / 2;
  }

  return (
    CHART_PADDING.top +
    ((maxValue - value) / (maxValue - minValue)) * chartHeight
  );
};

const getChartX = (index, length) => {
  const chartWidth = CHART_WIDTH - CHART_PADDING.left - CHART_PADDING.right;

  if (length <= 1) {
    return CHART_WIDTH / 2;
  }

  return CHART_PADDING.left + (index / (length - 1)) * chartWidth;
};

const getNiceStep = (rawStep) => {
  if (!Number.isFinite(rawStep) || rawStep <= 0) {
    return 1;
  }

  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const normalizedStep = rawStep / magnitude;

  if (normalizedStep <= 1) {
    return magnitude;
  }

  if (normalizedStep <= 2) {
    return 2 * magnitude;
  }

  if (normalizedStep <= 2.5) {
    return 2.5 * magnitude;
  }

  if (normalizedStep <= 5) {
    return 5 * magnitude;
  }

  return 10 * magnitude;
};

const getYAxisConfig = (minValue, maxValue) => {
  const range = Math.max(maxValue - minValue, 1);
  const step = getNiceStep(range / 4);

  let axisMin = Math.floor(minValue / step) * step;
  let axisMax = Math.ceil(maxValue / step) * step;

  axisMin = Math.min(axisMin, 0);
  axisMax = Math.max(axisMax, 0);

  if (axisMin === axisMax) {
    axisMin -= step;
    axisMax += step;
  }

  const values = [];

  for (let value = axisMax; value >= axisMin; value -= step) {
    const roundedValue = Number(value.toFixed(10));
    values.push(Object.is(roundedValue, -0) ? 0 : roundedValue);
  }

  if (!values.includes(0)) {
    values.push(0);
  }

  const labels = Array.from(new Set(values)).sort((a, b) => b - a);

  return {
    axisMin,
    axisMax,
    labels,
  };
};

const getLineColorClass = (value) => {
  return value < 0
    ? "trading-pnl-chart__active-line--negative"
    : "trading-pnl-chart__active-line--positive";
};

const getDotColorClass = (value) => {
  return value < 0
    ? "trading-pnl-chart__active-dot--negative"
    : "trading-pnl-chart__active-dot--positive";
};

const getTooltipColorClass = (value) => {
  return value < 0
    ? "trading-pnl-chart__tooltip-value--negative"
    : "trading-pnl-chart__tooltip-value--positive";
};

const getHasTradingActivity = (tradingStats, points) => {
  const totalTrades = Number(tradingStats?.totalTrades) || 0;
  const wins = Number(tradingStats?.wins) || 0;
  const losses = Number(tradingStats?.losses) || 0;

  return (
    totalTrades > 0 ||
    wins > 0 ||
    losses > 0 ||
    points.some((point) => Number(point.pnl) !== 0)
  );
};

export const TradingPnlChart = ({ data, tradingStats }) => {
  const { __ } = useTranslation();
  const chartWrapRef = useRef(null);
  const chartSvgRef = useRef(null);
  const [activePoint, setActivePoint] = useState(null);

  const chartData = useMemo(() => {
    const points = data?.points || [];

    if (!points.length) {
      return null;
    }

    const hasTradingActivity = getHasTradingActivity(tradingStats, points);
    const pnlValues = points.map((point) => Number(point.pnl) || 0);

    const rawMinValue = Math.min(Number(data?.minPnl) || 0, ...pnlValues, 0);
    const rawMaxValue = Math.max(Number(data?.maxPnl) || 0, ...pnlValues, 0);
    const yAxisConfig = getYAxisConfig(rawMinValue, rawMaxValue);

    const preparedPoints = points.map((point, index) => {
      const pnl = Number(point.pnl) || 0;

      return {
        ...point,
        pnl,
        x: getChartX(index, points.length),
        y: getChartY(pnl, yAxisConfig.axisMin, yAxisConfig.axisMax),
      };
    });

    const linePath = preparedPoints
      .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
      .join(" ");

    const areaPath = `
      ${linePath}
      L ${preparedPoints[preparedPoints.length - 1].x} ${
      CHART_HEIGHT - CHART_PADDING.bottom
    }
      L ${preparedPoints[0].x} ${CHART_HEIGHT - CHART_PADDING.bottom}
      Z
    `;

    const yAxisLabels = yAxisConfig.labels.map((value) => ({
      value,
      y: getChartY(value, yAxisConfig.axisMin, yAxisConfig.axisMax),
    }));

    const interval = data?.interval || "daily";
    const spanDays = getPeriodSpanDays(points);
    const xAxisLabels = getXAxisLabels(points, interval, spanDays, __);
    const baselineY = getChartY(0, yAxisConfig.axisMin, yAxisConfig.axisMax);
    const positiveClipHeight = Math.max(baselineY - CHART_PADDING.top, 0);
    const negativeClipHeight = Math.max(
      CHART_HEIGHT - CHART_PADDING.bottom - baselineY,
      0,
    );

    return {
      points: preparedPoints,
      linePath,
      areaPath,
      yAxisLabels,
      xAxisLabels,
      interval,
      maxPnl: Number(data?.maxPnl) || 0,
      minPnl: Number(data?.minPnl) || 0,
      hasTradingActivity,
      baselineY,
      positiveClipHeight,
      negativeClipHeight,
      isSinglePoint: preparedPoints.length === 1,
    };
  }, [__, data, tradingStats]);

  useEffect(() => {
    if (chartData?.isSinglePoint && chartData.hasTradingActivity) {
      setActivePoint(chartData.points[0]);
    }
  }, [chartData]);

  useEffect(() => {
    if (chartData && !chartData.hasTradingActivity) {
      setActivePoint(null);
    }
  }, [chartData]);

  const getActivePointPosition = useCallback((point) => {
    const wrapRect = chartWrapRef.current?.getBoundingClientRect();
    const svgRect = chartSvgRef.current?.getBoundingClientRect();

    if (!wrapRect || !svgRect) {
      return null;
    }

    return {
      left:
        svgRect.left - wrapRect.left + (point.x / CHART_WIDTH) * svgRect.width,
      top:
        svgRect.top - wrapRect.top + (point.y / CHART_HEIGHT) * svgRect.height,
    };
  }, []);

  const getActivePointViewportPosition = useCallback((point) => {
    const svgRect = chartSvgRef.current?.getBoundingClientRect();

    if (!svgRect) {
      return null;
    }

    return {
      left: svgRect.left + (point.x / CHART_WIDTH) * svgRect.width,
      top: svgRect.top + (point.y / CHART_HEIGHT) * svgRect.height,
    };
  }, []);

  const handleChartMouseMove = useCallback(
    (event) => {
      if (!chartData?.hasTradingActivity || chartData?.isSinglePoint) {
        return;
      }

      const points = chartData.points;

      if (!points.length) {
        return;
      }

      const rect = event.currentTarget.getBoundingClientRect();
      const mouseX = ((event.clientX - rect.left) / rect.width) * CHART_WIDTH;

      const nearestPoint = points.reduce((closestPoint, point) => {
        const closestDistance = Math.abs(closestPoint.x - mouseX);
        const currentDistance = Math.abs(point.x - mouseX);

        return currentDistance < closestDistance ? point : closestPoint;
      }, points[0]);

      setActivePoint(nearestPoint);
    },
    [chartData],
  );

  const handleChartMouseLeave = useCallback(() => {
    if (chartData?.isSinglePoint) {
      return;
    }

    setActivePoint(null);
  }, [chartData]);

  const displayMaxPnl = chartData?.maxPnl ?? (Number(data?.maxPnl) || 0);
  const displayMinPnl = chartData?.minPnl ?? (Number(data?.minPnl) || 0);
  const visibleActivePoint = activePoint;
  const activePointPosition = visibleActivePoint
    ? getActivePointPosition(visibleActivePoint)
    : null;
  const activePointViewportPosition = visibleActivePoint
    ? getActivePointViewportPosition(visibleActivePoint)
    : null;

  useEffect(() => {
    if (!activePoint) {
      return undefined;
    }

    const hideTooltipOnScroll = () => {
      setActivePoint(null);
    };

    window.addEventListener("scroll", hideTooltipOnScroll, true);
    window.addEventListener("wheel", hideTooltipOnScroll, { passive: true });
    window.addEventListener("touchmove", hideTooltipOnScroll, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", hideTooltipOnScroll, true);
      window.removeEventListener("wheel", hideTooltipOnScroll);
      window.removeEventListener("touchmove", hideTooltipOnScroll);
    };
  }, [activePoint]);

  const getTooltipViewportLeft = (left) => {
    if (typeof window === "undefined") {
      return left;
    }

    const tooltipWidth =
      window.innerWidth <= 760 ? MOBILE_TOOLTIP_WIDTH : TOOLTIP_WIDTH;

    const minLeft = tooltipWidth / 2 + TOOLTIP_SCREEN_OFFSET;
    const maxLeft =
      window.innerWidth - tooltipWidth / 2 - TOOLTIP_SCREEN_OFFSET;

    return Math.min(Math.max(left, minLeft), maxLeft);
  };

  return (
    <div className="trading-pnl-chart">
      <div className="trading-pnl-chart__header">
        <h3 className="trading-pnl-chart__title">
          {__("trading_profile.pnl_chart_title")}
        </h3>

        {chartData?.hasTradingActivity && (
          <div className="trading-pnl-chart__badges">
            <div className="trading-pnl-chart__badge">
              Max PnL: {formatCurrency(displayMaxPnl)}
            </div>

            <div className="trading-pnl-chart__badge">
              Min PnL: {formatCurrency(displayMinPnl)}
            </div>
          </div>
        )}
      </div>

      <div
        className={`trading-pnl-chart__body ${
          chartData && !chartData.hasTradingActivity
            ? "trading-pnl-chart__body--no-activity"
            : ""
        }`}
      >
        {!chartData ? (
          <div className="trading-pnl-chart__empty">No chart data</div>
        ) : !chartData.hasTradingActivity ? (
          <div className="trading-pnl-chart__no-activity">
            <p className="trading-pnl-chart__no-activity-title">
              {__("trading_profile.pnl_chart_no_activity")}
            </p>

            <p className="trading-pnl-chart__no-activity-hint">
              {__("trading_profile.pnl_chart_no_activity_hint")}
            </p>
          </div>
        ) : (
          <>
            <div className="trading-pnl-chart__chart-wrap" ref={chartWrapRef}>
              <svg
                ref={chartSvgRef}
                className="trading-pnl-chart__svg trading-pnl-chart__svg--interactive"
                viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
                preserveAspectRatio="none"
                onMouseMove={handleChartMouseMove}
                onMouseLeave={handleChartMouseLeave}
              >
                <defs>
                  <linearGradient
                    id="tradingPnlChartGradientPositive"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#2FA766" stopOpacity="0.45" />
                    <stop offset="55%" stopColor="#2FA766" stopOpacity="0.14" />
                    <stop offset="100%" stopColor="#2FA766" stopOpacity="0" />
                  </linearGradient>

                  <linearGradient
                    id="tradingPnlChartGradientNegative"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="0%" stopColor="#F04438" stopOpacity="0.3" />
                    <stop offset="55%" stopColor="#F04438" stopOpacity="0.12" />
                    <stop offset="100%" stopColor="#F04438" stopOpacity="0" />
                  </linearGradient>

                  <clipPath id="tradingPnlChartPositiveClip">
                    <rect
                      x={CHART_PADDING.left}
                      y={CHART_PADDING.top}
                      width={
                        CHART_WIDTH - CHART_PADDING.left - CHART_PADDING.right
                      }
                      height={Math.max(chartData.positiveClipHeight - 2, 0)}
                    />
                  </clipPath>

                  <clipPath id="tradingPnlChartNegativeClip">
                    <rect
                      x={CHART_PADDING.left}
                      y={chartData.baselineY + 2}
                      width={
                        CHART_WIDTH - CHART_PADDING.left - CHART_PADDING.right
                      }
                      height={Math.max(chartData.negativeClipHeight - 2, 0)}
                    />
                  </clipPath>
                </defs>

                <line
                  x1={CHART_PADDING.left}
                  x2={CHART_WIDTH - CHART_PADDING.right}
                  y1={chartData.baselineY}
                  y2={chartData.baselineY}
                  className="trading-pnl-chart__baseline"
                  vectorEffect="non-scaling-stroke"
                />

                {!chartData.isSinglePoint && (
                  <>
                    <path
                      d={chartData.areaPath}
                      className="trading-pnl-chart__area trading-pnl-chart__area--positive"
                      clipPath="url(#tradingPnlChartPositiveClip)"
                    />

                    <path
                      d={chartData.areaPath}
                      className="trading-pnl-chart__area trading-pnl-chart__area--negative"
                      clipPath="url(#tradingPnlChartNegativeClip)"
                    />

                    <path
                      d={chartData.linePath}
                      className="trading-pnl-chart__line trading-pnl-chart__line--positive"
                      clipPath="url(#tradingPnlChartPositiveClip)"
                    />

                    <path
                      d={chartData.linePath}
                      className="trading-pnl-chart__line trading-pnl-chart__line--negative"
                      clipPath="url(#tradingPnlChartNegativeClip)"
                    />
                  </>
                )}

                {visibleActivePoint && (
                  <>
                    <line
                      x1={visibleActivePoint.x}
                      x2={visibleActivePoint.x}
                      y1={CHART_PADDING.top}
                      y2={CHART_HEIGHT - CHART_PADDING.bottom}
                      className={`trading-pnl-chart__active-line ${getLineColorClass(
                        visibleActivePoint.pnl,
                      )}`}
                      vectorEffect="non-scaling-stroke"
                    />

                    <line
                      x1={CHART_PADDING.left}
                      x2={CHART_WIDTH - CHART_PADDING.right}
                      y1={visibleActivePoint.y}
                      y2={visibleActivePoint.y}
                      className={`trading-pnl-chart__active-line ${getLineColorClass(
                        visibleActivePoint.pnl,
                      )}`}
                      vectorEffect="non-scaling-stroke"
                    />
                  </>
                )}
              </svg>

              <div className="trading-pnl-chart__y-axis" aria-hidden="true">
                {chartData.yAxisLabels.map((label) => (
                  <span
                    key={`y-label-${label.value}`}
                    className={`trading-pnl-chart__y-axis-label ${
                      label.value === 0
                        ? "trading-pnl-chart__y-axis-label--zero"
                        : ""
                    }`}
                    style={{ top: `${(label.y / CHART_HEIGHT) * 100}%` }}
                  >
                    {formatAxisCurrency(label.value)}
                  </span>
                ))}
              </div>

              {!chartData.isSinglePoint && activePointPosition && (
                <span
                  className={`trading-pnl-chart__active-dot ${getDotColorClass(
                    visibleActivePoint?.pnl,
                  )}`}
                  style={{
                    left: activePointPosition.left,
                    top: activePointPosition.top,
                  }}
                  aria-hidden="true"
                />
              )}
            </div>

            <div
              className={`trading-pnl-chart__x-axis ${
                chartData.xAxisLabels.length <= 1
                  ? "trading-pnl-chart__x-axis--single"
                  : ""
              }`}
              aria-hidden="true"
            >
              {chartData.xAxisLabels.map((axisLabel) => (
                <span
                  key={`x-label-${axisLabel.date}-${axisLabel.index}`}
                  className="trading-pnl-chart__x-axis-label"
                >
                  {axisLabel.label}
                </span>
              ))}
            </div>

            {visibleActivePoint &&
              activePointViewportPosition &&
              createPortal(
                <div
                  className={`trading-pnl-chart__tooltip ${
                    chartData.isSinglePoint
                      ? "trading-pnl-chart__tooltip--static"
                      : ""
                  }`}
                  style={{
                    left: getTooltipViewportLeft(
                      activePointViewportPosition.left,
                    ),
                    top: activePointViewportPosition.top,
                  }}
                >
                  <div className="trading-pnl-chart__tooltip-head">
                    <span>{__("trading_profile.pnl_chart_tooltip_time")}</span>
                    <span>{__("trading_profile.pnl_chart_tooltip_pnl")}</span>
                  </div>

                  <div className="trading-pnl-chart__tooltip-values">
                    <strong>
                      {formatTooltipDateLabel(
                        visibleActivePoint.date,
                        __,
                        chartData.interval,
                      )}
                    </strong>

                    <strong
                      className={getTooltipColorClass(visibleActivePoint.pnl)}
                    >
                      {formatCurrency(visibleActivePoint.pnl)}
                    </strong>
                  </div>
                </div>,
                document.body,
              )}
          </>
        )}
      </div>
    </div>
  );
};

export default TradingPnlChart;
